import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { isAppleSignInConfigured, isAppleSignInCancelled, signInWithApple } from '../../utils/appleSignIn';
import styles from './AuthShell.module.css';

const GOOGLE_BUTTON_MAX_WIDTH = 400;

let googleInitialized = false;
// GIS is initialised once per page load; the active callback is swapped so
// whichever auth page is mounted receives the credential.
let activeGoogleCallback = null;

export function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
    );
}

export function AppleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
            <path d="M16.365 1.43c0 1.14-.42 2.1-1.26 2.88-.87.86-1.92 1.35-3.06 1.27-.05-1.1.38-2.11 1.2-2.93.84-.85 1.94-1.33 3.06-1.4.03.06.06.12.06.18zM20.7 17.2c-.37.86-.8 1.65-1.32 2.37-.7.98-1.27 1.66-1.72 2.04-.69.63-1.43.96-2.22.98-.57 0-1.25-.16-2.05-.49-.8-.33-1.54-.49-2.21-.49-.7 0-1.46.16-2.28.49-.82.33-1.48.5-1.98.52-.76.03-1.51-.3-2.26-1.01-.49-.42-1.08-1.13-1.79-2.14-.76-1.07-1.38-2.32-1.87-3.74C.45 14.2.2 12.7.2 11.25c0-1.66.36-3.09 1.08-4.29a6.34 6.34 0 0 1 2.26-2.28 6.09 6.09 0 0 1 3.06-.86c.6 0 1.4.19 2.38.56.98.37 1.61.56 1.89.56.21 0 .91-.22 2.1-.65 1.13-.4 2.08-.57 2.86-.5 2.12.17 3.71 1 4.77 2.5-1.9 1.15-2.84 2.76-2.82 4.83.02 1.61.6 2.95 1.75 4.02.52.49 1.1.87 1.74 1.15-.14.4-.28.79-.44 1.17z"/>
        </svg>
    );
}

/**
 * SocialAuthButtons
 *
 * Apple + Google entry points shared by the login and signup pages.
 *
 * - Google uses Google Identity Services' rendered button (unchanged flow);
 *   the ID token credential is handed to `onGoogleCredential`.
 * - Apple uses Sign in with Apple JS and hands the *raw* identity token to
 *   `onAppleIdentity`. Verification happens on the backend only.
 * - Neither provider's handler lives here: pages pass the existing
 *   AuthContext actions in, so no auth logic is duplicated.
 */
export default function SocialAuthButtons({
    mode = 'login',
    onGoogleCredential,
    onAppleIdentity,
    onError,
    disabled = false,
}) {
    const { isDark } = useTheme();
    const googleBtnRef = useRef(null);
    const googleWrapRef = useRef(null);
    const callbackRef = useRef(null);
    const [googleRendered, setGoogleRendered] = useState(false);
    const [googleWidth, setGoogleWidth] = useState(GOOGLE_BUTTON_MAX_WIDTH);
    const [appleBusy, setAppleBusy] = useState(false);
    const appleConfigured = isAppleSignInConfigured();

    callbackRef.current = async (response) => {
        trackEvent(ANALYTICS_EVENTS.AUTH_METHOD_SELECTED, { platform: 'web', mode, method: 'google' });
        try {
            await onGoogleCredential?.(response?.credential);
        } catch (err) {
            onError?.(err?.message || 'Google sign-in failed. Please try again.');
        }
    };

    // Measure the container so the GIS button can fill it (GIS needs a pixel width).
    useEffect(() => {
        const measure = () => {
            const width = googleWrapRef.current?.getBoundingClientRect?.().width;
            if (!width) return;
            const next = Math.max(200, Math.min(GOOGLE_BUTTON_MAX_WIDTH, Math.floor(width)));
            setGoogleWidth((prev) => (Math.abs(prev - next) > 8 ? next : prev));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    useEffect(() => {
        activeGoogleCallback = (response) => callbackRef.current?.(response);
        return () => { if (activeGoogleCallback) activeGoogleCallback = null; };
    }, []);

    useEffect(() => {
        const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!window.google?.accounts?.id || !googleClientId || !googleBtnRef.current) return;

        if (!googleInitialized) {
            window.google.accounts.id.initialize({
                client_id: googleClientId,
                callback: (response) => activeGoogleCallback?.(response),
            });
            googleInitialized = true;
        }

        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
            theme: isDark ? 'filled_black' : 'outline',
            size: 'large',
            shape: 'pill',
            width: googleWidth,
            text: 'continue_with',
            logo_alignment: 'left',
        });
        setGoogleRendered(true);
    }, [isDark, googleWidth]);

    const handleApple = async () => {
        if (appleBusy || disabled) return;
        trackEvent(ANALYTICS_EVENTS.AUTH_METHOD_SELECTED, { platform: 'web', mode, method: 'apple' });
        setAppleBusy(true);
        try {
            const identity = await signInWithApple();
            await onAppleIdentity?.(identity);
        } catch (err) {
            if (!isAppleSignInCancelled(err)) {
                onError?.(err?.message || 'Apple sign-in failed. Please try again.');
            }
        } finally {
            setAppleBusy(false);
        }
    };

    return (
        <div className={styles.socialStack} data-testid="social-auth-buttons">
            {appleConfigured && (
                <button
                    type="button"
                    className={styles.btnApple}
                    onClick={handleApple}
                    disabled={disabled || appleBusy}
                    aria-label="Continue with Apple"
                    aria-busy={appleBusy || undefined}
                >
                    {appleBusy ? <span className={styles.spinner} aria-hidden="true" /> : <AppleIcon />}
                    <span>Continue with Apple</span>
                </button>
            )}

            <div className={styles.googleWrap} ref={googleWrapRef}>
                {!googleRendered && (
                    <button
                        type="button"
                        className={styles.btnGooglePlaceholder}
                        disabled
                        aria-label="Continue with Google (unavailable)"
                        title="Google sign-in is loading or unavailable"
                    >
                        <GoogleIcon />
                        <span>Continue with Google</span>
                    </button>
                )}
                <div ref={googleBtnRef} className={styles.googleTarget} aria-label="Continue with Google" />
            </div>
        </div>
    );
}

export function __resetGoogleForTests() {
    googleInitialized = false;
    activeGoogleCallback = null;
}
